/**
 * Generator markdown exportu design systemu EcomFlow.
 * Eksport zawiera tokeny (tailwind.config.js snapshot) + listę komponentów
 * + reguły używania. Plik .md do wklejenia do LLM albo do docs.
 */

const TOKENS_COLORS = `## Kolory

### Surface (tła)

| Token | Hex | Użycie |
|---|---|---|
| \`surface.base\`    | #16161c | Body background |
| \`surface.raised\`  | #1e1e26 | Karty, panele, modale |
| \`surface.hover\`   | #26262f | Hover na surface |
| \`surface.muted\`   | #2e2e38 | Drugi poziom hover |
| \`surface.overlay\` | #363642 | Tooltips, context menus |
| \`surface.bright\`  | #464654 | Rzadko - najjaśniejszy |

### Text

| Token | Hex | Użycie |
|---|---|---|
| \`text.primary\`   | #ffffff | Główny tekst |
| \`text.secondary\` | #d1d5db | Labels, opisy (gray-300) |
| \`text.muted\`     | #9ca3af | Placeholdery, captions (gray-400) |
| \`text.disabled\`  | #6b7280 | Disabled states (gray-500) |

**Zasada:** poniżej text.disabled nie schodzimy (accessibility).

### Accent (marka)

| Token | Hex | Użycie |
|---|---|---|
| \`accent.primary\`   | #4a9eff | CTA, linki, focus, primary button |
| \`accent.secondary\` | #6b8cce | Gradienty, akcent uzupełniający |
| \`accent.tertiary\`  | #7a8fba | Rzadki akcent |
| \`accent.deep\`      | #1e3a5f | Hover na accent, gradient end |
| \`accent.info\`      | #3b82f6 | Info accent |

### Status

Każdy status: \`DEFAULT\` (solid fill), \`bg\` (20% opacity), \`border\` (30% opacity).

| Token | Hex | Użycie |
|---|---|---|
| \`success\` | #10b981 | Zapłacone, operacja OK |
| \`warning\` | #f59e0b | Ostrzeżenia, pending |
| \`danger\`  | #ef4444 | Błędy, destructive |
| \`info\`    | #3b82f6 | Info messages |

### Border

| Token | Value | Użycie |
|---|---|---|
| \`border.subtle\`  | rgba(255,255,255,0.06) | Default delikatny |
| \`border.DEFAULT\` | rgba(255,255,255,0.10) | Widoczny |
| \`border.strong\`  | rgba(255,255,255,0.15) | Mocniejszy |
| \`border.accent\`  | rgba(74,158,255,0.30)  | Focus/active |
`;

const TOKENS_TYPO = `## Typografia

**Font family:** Inter (Google Fonts). Fallback: system-ui, sans-serif.

### Skala

| Klasa | Rozmiar | Line-height | Użycie |
|---|---|---|---|
| \`text-xxs\`  | 10px | 14px | Micro labels, uppercase captions, timestamps |
| \`text-xs\`   | 11px | 16px | Caption, badge, meta-info (override Tailwind 12) |
| \`text-sm\`   | 14px | 20px | Body default |
| \`text-base\` | 16px | 24px | Body w długich tekstach |
| \`text-lg\`   | 18px | 28px | Podtytuły, card header |
| \`text-xl\`   | 20px | 28px | H3 |
| \`text-2xl\`  | 24px | 32px | H2 |
| \`text-3xl\`  | 30px | 36px | H1 strony (PageHeader) |
| \`text-4xl\`  | 36px | 40px | Display |

### Wagi

300 (light - rzadko), 400 (regular - body), 500 (medium - emphasis), 600 (semibold - buttons, subheadings), 700 (bold - H1/H2/H3), 800 (extrabold - rzadko hero).
`;

const TOKENS_SPACING = `## Spacing, radius, motion

### Spacing

Tailwind default scale (4px base). Trzymaj się: 4 / 8 / 12 / 16 / 24 / 32. Nie wymyślaj p-5 albo gap-7.

Semantyczne aliasy:
- \`field-x\` (16px) - padding poziomy inputów/buttonów
- \`field-y\` (10px) - padding pionowy
- \`p-card\` (24px) - padding kart
- \`space-y-section\` (32px) - między sekcjami

### Radius

| Klasa | Rozmiar | Użycie |
|---|---|---|
| \`rounded-md\`    | 6px    | Drobne inputs, tagi |
| \`rounded-lg\`    | 8px    | Secondary buttons, badges |
| \`rounded-xl\`    | 12px   | **Default** buttons, inputs, modale |
| \`rounded-2xl\`   | 16px   | Większe kontenery (.card) |
| \`rounded-full\`  | pill   | Pill badges, avatars |

Semantyczne aliasy: \`rounded-btn\`, \`rounded-input\`, \`rounded-card\`, \`rounded-badge\`.

### Motion

- \`duration-fast\` (150ms) - hover, focus
- \`duration-normal\` (250ms) - enter/exit
- \`duration-slow\` (400ms) - decorative
- \`ease-smooth\` cubic-bezier(0.4, 0, 0.2, 1) - Material default
- \`ease-bounce-in\` cubic-bezier(0.34, 1.56, 0.64, 1) - lekki overshoot
`;

const COMPONENTS = `## Komponenty (packages/frontend/src/components/ui/)

### Layout
- **PageHeader** - nagłówek strony (ikona + title + subtitle + actions). Akcentowane słowo w tytule przez \`accentWord\`.
- **BackLink** - "← Wróć do X", umieszczaj NAD PageHeader.
- **Card** - kontener treści. Warianty: default / interactive (hover glow + klik) / elevated (shadow-glass).
- **SettingsSection** - karta sekcji ustawień (icon + title + subtitle + content). Prop \`flushContent\` dla listy ToggleRow, \`dense\` dla zagnieżdżonych.
- **Divider** - horizontal (opcjonalny label środkowy) / vertical (inline w toolbarze).

### Formularze
- **Button** - variants primary / secondary / ghost / danger / icon × sizes sm / md / lg. \`loading\`, \`leftIcon\`, \`rightIcon\`, \`fullWidth\`.
- **Input** - wszystkie typy HTML, leftIcon/rightIcon, clearable (X kasuje), error state.
- **Textarea** - opcjonalny \`autoResize\` z minRows/maxRows.
- **Select** - native z custom chevronem (mobile picker działa).
- **Checkbox** - custom styling dark-mode friendly, klikalna etykieta.
- **Radio** + **RadioGroup<T>** - generic type-safe grupa, vertical/horizontal.
- **Toggle** - iOS-style pill switch, sizes sm/md.
- **ToggleRow** - wiersz label + description + toggle (lub action). Łącz z SettingsSection flushContent.
- **FormField** - wrapper label + children + helper/error (error ma priorytet).
- **Slider** + **BreakpointSlider** - numeric range (native), BreakpointSlider to 3 slidery Desktop/Tablet/Mobile.

### Nawigacja
- **Tabs** (composable - Tabs / TabsList / TabsTrigger / TabsContent) - główna nawigacja, keyboard nav.
- **SegmentedTabs<T>** - pill-style mini tabs (breakpoint switcher, period filter). NIE dla głównej nawigacji.
- **Breadcrumbs** - ścieżka z ChevronRight.
- **Pagination** - prev/next + numery z ellipsis + opcjonalny pageSize.
- **Modal** - dialog portal (Escape + backdrop zamykają, lock body scroll).
- **Tooltip** - hover + focus z delay.
- **Dropdown** - anchored menu, keyboard nav (↑↓ Enter Esc).
- **Popover** - jak Dropdown ale dowolna zawartość.
- **ContextMenu** - right-click, portal, viewport-aware clamping.

### Status / feedback
- **Badge** - warianty neutral / success / warning / danger / info / accent, sizes sm/md.
- **StatCard** - KPI tile z emoji + label + value + opcjonalna delta trend.
- **Alert** - inline banner (nie auto-hide).
- **Toast** + \`useToast()\` hook - auto-hide, stack w top-right.
- **Loader** - spinner z opcjonalnym labelem.
- **Skeleton** - placeholder dla loading states.
- **StatusIndicator** - kropka online/offline/busy z opcjonalnym pulse.
- **EmptyState** - pusty stan z ikoną + title + description + button.

### Dane
- **Table<T>** - generyczna tabela z controlled sort, render prop, loading skeleton.
- **DataList** - pary label/value (horizontal/vertical), opcjonalne hint pod value.

### Specjalne
- **SearchInput** - Input z Search ikoną + clearable + opcjonalny onDebouncedChange.
- **CopyButton** - klik kopiuje value, ikona Copy → Check przez 2s + toast.
- **ColorSwatch** - preview koloru z checkerboard bg (widać alpha).
- **FileUpload** - drag & drop + file picker, walidacja maxSize.
- **Accordion** - expand/collapse, single/multi open.
`;

const RULES = `## Reguły (docs/design-system.md sekcja 10)

### Button - kiedy który

- **primary**: główna akcja (1 na ekran/modal). "Zapisz", "Wyślij", "Dodaj".
- **secondary**: drugorzędna, równoważna wizualnie ale mniej prominentna. "Eksportuj", "Podgląd".
- **ghost**: niskoinwazyjne, cancel, zamknięcie. "Anuluj", "Pomiń".
- **danger**: destructive (nieodwracalne). "Usuń", "Wyczyść wszystko".
- **icon**: toolbar/actions row, tylko ikona + \`aria-label\` obowiązkowy.

**Rozmiary**: sm → tabele, toolbary, inline. md (default) → formularze, CTA. lg → hero, landing.

### Header hierarchy

1. **PageHeader** - text-3xl, top of page. Opcjonalnie poprzedzony BackLink.
2. **SettingsSection title** - text-lg, w środku strony.
3. **Card header** - text-lg, w kartach detail.
4. **Podsekcje h3** - text-lg w SettingsSection.

Nie używaj h1/h2 samodzielnie - owiń w PageHeader / SettingsSection.

### Button placement w footerach modali

**Primary LEWO, Cancel PRAWO**, \`justify-between\` dla rozsunięcia (redukuje misclicki):

\`\`\`tsx
<div className="flex items-center justify-between gap-2">
  <Button variant="primary">Zapisz</Button>
  <Button variant="ghost">Anuluj</Button>
</div>
\`\`\`

### Settings page anatomy

\`\`\`tsx
<BackLink to="/parent" label="Wróć do..." />   {/* opt */}
<PageHeader icon={Settings} title="Ustawienia X" subtitle="..." />
<div className="space-y-6">
  <SettingsSection icon={Layout} title="Układ" subtitle="...">
    <FormField label="..."><Input /></FormField>
  </SettingsSection>
  <SettingsSection icon={Bell} title="Powiadomienia" flushContent>
    <ToggleRow label="..." description="..." />
    <ToggleRow label="..." description="..." />
  </SettingsSection>
</div>
{/* Sticky save bar */}
\`\`\`

### Microcopy / tone of voice

- **Język UI**: polski.
- **Ton**: bezpośredni, rzeczowy. Operator SaaS to profesjonalista.
- **Bez wykrzykników** ("Zapisano!" → "Zapisano").
- **Bez emoji** poza status indicators.
- **Bez em-dash —** w UI (używaj - lub –).
- **Imperative verbs** na buttonach: "Zapisz", "Usuń", "Wyślij".
- **Errors**: co się stało + co zrobić. "Nie udało się zapisać. Spróbuj ponownie."

### Zakazy

- Surowe hexy dla kolorów marki (użyj tokenów Tailwind).
- \`window.confirm / alert / prompt\` (użyj ConfirmDialog).
- Em-dash — w UI.
- Custom rozmiary text-[Xpx] - trzymaj się skali.
- Mieszanie \`.btn-primary\` z \`<Button>\` - jeden system na 1 stronę.
`;

export function generateDesignSystemMarkdown(): string {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `# EcomFlow Design System

**Snapshot:** ${timestamp}
**Projekt:** EcomFlow (multi-tenant SaaS, Polish e-commerce)
**Tech stack:** React 18 + Tailwind 3 + TypeScript + Vite 6
**Mode:** Dark mode only

Dokument to eksport z living style guide \`/branding\` w aplikacji. Jeden plik
do wklejenia do LLM / dzielenia ze współpracownikami / jako referencja przy
projektowaniu nowych funkcji.

---

${TOKENS_COLORS}

---

${TOKENS_TYPO}

---

${TOKENS_SPACING}

---

${COMPONENTS}

---

${RULES}

---

## Źródła prawdy

- **Tokeny**: \`packages/frontend/tailwind.config.js\`
- **Komponenty**: \`packages/frontend/src/components/ui/\`
- **Reguły**: \`docs/design-system.md\`
- **Living guide**: w aplikacji → Panel administratora → zakładka Branding
`;
}

export function downloadDesignSystemMarkdown(): void {
  const md = generateDesignSystemMarkdown();
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const timestamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `ecomflow-design-system-${timestamp}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
